import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ComplianceChecklistItemStatus } from '../../../../generated/core-api/graphql-react-query';
import usePrevious from '../../../../Hooks/usePrevious';
import sendNotification from '../../../../notifications/sendNotification';
import lexicalToString from '../../../../utils/lexicalToString';
import { getProjectTrigger } from '../../../Components/MainWindow/Components/Pages/CommandPage/Hooks/useCommands';
import { useUserInfoData } from '../../../Hooks/useUserInfo';
import useSessionComplianceContext from '../Contexts/SessionComplianceContextProvider/useSessionComplianceContext';
import useSessionStoreContext from '../Contexts/SessionStoreContextProvider/useSessionStoreContext';

export default function useComplianceNotifications() {
  const { sessionId: [sessionId] } = useSessionStoreContext();
  const complianceChecklist = useSessionComplianceContext();
  const previous = usePrevious(complianceChecklist);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('notifications');
  const notificationSettings = useUserInfoData().appSettings?.notifications;

  const newlyCompleted = React.useMemo(
    () => complianceChecklist.filter((topic) => {
      if (topic.result !== ComplianceChecklistItemStatus.Validated) return false;
      const prevTopic = previous.find((item) => (item.textLower === topic.textLower));
      return prevTopic?.result !== ComplianceChecklistItemStatus.Validated;
    }),
    [complianceChecklist, previous],
  );

  const newlyIncluded = React.useMemo(
    () => complianceChecklist.filter((topic) => !previous?.find((prevTopic) => (prevTopic.textLower === topic.textLower))),
    [complianceChecklist, previous],
  );

  const previousCompleted = usePrevious(newlyCompleted);
  const previousIncluded = usePrevious(newlyIncluded);

  React.useEffect(() => {
    if (!sessionId) return;
    if (previousCompleted !== newlyCompleted && newlyCompleted.length > previousCompleted.length) {
      newlyCompleted.forEach((topic) => {
        const title = topic.topic.text;
        enqueueSnackbar(t('completed-compliance-task.title', { title }), { variant: 'success' });
      });
    }
  }, [previousCompleted, newlyCompleted, !!sessionId]);

  React.useEffect(() => {
    if (!sessionId) return;
    if (previousIncluded !== newlyIncluded && newlyIncluded.length > previousIncluded.length) {
      newlyIncluded.forEach((topic) => {
        const title = topic.topic.text;

        if (notificationSettings?.notifyOnNewMandatoryTopic) {
          const commands = topic.topic.projectVocalTriggers?.items
            .map((item) => getProjectTrigger(item)?.text)
            .filter((command): command is string => !!command);
          let instructions;
          if (topic.complianceExample) {
            instructions = lexicalToString(topic.complianceExample).join('\n');
          }
          sendNotification({
            type: 'new-compliance-task',
            title,
            commands,
            instructions,
          });
        }
        enqueueSnackbar(t('new-compliance-task.title', { title }), { variant: 'info' });
      });
    }
  }, [previousIncluded, newlyIncluded, !!sessionId]);
}
